"use client";

import { useIsTouchDevice } from "@dc/hooks";
import { Modal as HeroModal } from "@heroui/react";
import { useRouter } from "next/navigation";
import {
  type ComponentProps,
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "../../lib/utils";
import type { CurrentModalContextType, DCModalProps, ModalCloseOptions, ModalContextType } from "./Modal.types";

const ModalContext = createContext<ModalContextType | null>(null);

/** Scoped to a single <Modal>, so its children can close the one they are inside. */
const CurrentModalContext = createContext<CurrentModalContextType | null>(null);

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [modals, setModals] = useState<Map<string, (options?: ModalCloseOptions) => void>>(new Map());

  const registerModal = useCallback((id: string, closeHandler: (options?: ModalCloseOptions) => void) => {
    setModals((prev) => new Map(prev).set(id, closeHandler));
  }, []);

  const unregisterModal = useCallback((id: string) => {
    setModals((prev) => {
      const newMap = new Map(prev);
      newMap.delete(id);
      return newMap;
    });
  }, []);

  const closeModal = useCallback(
    (id?: string, options?: ModalCloseOptions): void => {
      if (id) {
        const closeHandler = modals.get(id);
        if (closeHandler) {
          closeHandler(options);
        }
      } else {
        const entries = Array.from(modals.entries());
        const lastModal = entries[entries.length - 1];
        if (lastModal) {
          lastModal[1](options);
        }
      }
    },
    [modals],
  );

  return (
    <ModalContext.Provider value={{ registerModal, unregisterModal, closeModal }}>{children}</ModalContext.Provider>
  );
};

/**
 * The modal the caller is rendered inside, or null outside one. Children closing
 * themselves should use this rather than `closeModal()` with no id.
 */
export const useCurrentModal = () => useContext(CurrentModalContext);

export const useModalContext = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModalContext must be used within a ModalProvider");
  }
  return context;
};

export const Modal = ({
  className,
  classNames,
  onClose,
  skipBack = false,
  children,
  placement = "center",
  isDismissable,
  modalId: providedModalId,
  ...props
}: DCModalProps & ComponentProps<typeof HeroModal>) => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(true);
  const generatedModalId = useId();
  const modalId = providedModalId ?? generatedModalId;
  const { registerModal, unregisterModal } = useModalContext();
  const isTouchDevice = !!useIsTouchDevice();

  const closeHandlerRef = useRef<((options?: ModalCloseOptions) => void) | null>(null);

  const providerCloseHandler = useCallback(
    (options?: ModalCloseOptions) => {
      setIsOpen(false);
      if (skipBack || options?.skipBack) {
        return;
      }
      setTimeout(() => {
        router.back();
      }, 200);
    },
    [router, skipBack],
  );

  const handleClose = useCallback(
    (options?: ModalCloseOptions) => {
      onClose?.();
      providerCloseHandler(options);
    },
    [onClose, providerCloseHandler],
  );

  const currentModal = useMemo<CurrentModalContextType>(
    () => ({ modalId, close: handleClose }),
    [modalId, handleClose],
  );

  closeHandlerRef.current = providerCloseHandler;

  useEffect(() => {
    const stableCloseHandler = (options?: ModalCloseOptions) => {
      if (closeHandlerRef.current) {
        closeHandlerRef.current(options);
      }
    };

    registerModal(modalId, stableCloseHandler);
    return () => {
      unregisterModal(modalId);
    };
  }, [modalId, registerModal, unregisterModal]);

  return (
    <HeroModal
      className={cn("dc-modal-open", className)}
      classNames={{
        ...classNames,
        wrapper: cn("max-sm:items-center", classNames?.wrapper),
        base: cn("max-sm:mx-6 max-sm:my-8 max-sm:max-h-[calc(100dvh-4rem)]", classNames?.base),
        closeButton: cn(
          "cursor-pointer text-icon-neutral-onMatch-usual hover:bg-icon-neutral-onMatch-usual/10 active:bg-icon-neutral-onMatch-usual/50",
          classNames?.closeButton,
        ),
      }}
      isOpen={isOpen}
      onClose={handleClose}
      placement={placement}
      scrollBehavior="inside"
      backdrop="blur"
      isDismissable={isTouchDevice ? false : isDismissable}
      motionProps={{
        variants: {
          enter: {
            y: 0,
            opacity: 1,
            transition: {
              duration: 0.2,
              ease: "easeOut",
            },
          },
          exit: {
            y: -20,
            opacity: 0,
            transition: {
              duration: 0.2,
              ease: "easeIn",
            },
          },
        },
      }}
      {...props}
    >
      <CurrentModalContext.Provider value={currentModal}>{children}</CurrentModalContext.Provider>
    </HeroModal>
  );
};
